/**
 * Strategy tabs (issue #337): a strategy's universe — the stocks opted in via a
 * signal watch — opened as a list under My Lists, the same virtual-tab pattern
 * as label tabs (`label_<id>`). The tab id is `strategy_<strategies.id>`.
 *
 * ONE definition of "which watches belong to strategy S", shared by the
 * Strategies widget, the My Lists dropdown and App.js's loadData branch, so the
 * widget's watch count, the dropdown's count and the table's rows can never
 * disagree. Every watch carries `strategy_id` since #322; a row without one
 * (none exist after the backfill — defensive only) is owned by the band
 * strategy rather than dropped.
 */
import { resolveStopK } from '../shared/bandSignals';

export const STRATEGY_TAB_PREFIX = 'strategy_';

export const strategyTabId = (strategyId) => `${STRATEGY_TAB_PREFIX}${strategyId}`;

export const isStrategyTab = (tab) => typeof tab === 'string' && tab.startsWith(STRATEGY_TAB_PREFIX);

export const strategyIdFromTab = (tab) => {
  if (!isStrategyTab(tab)) return null;
  const id = Number(tab.slice(STRATEGY_TAB_PREFIX.length));
  return Number.isInteger(id) ? id : null;
};

export const findStrategyForTab = (strategies, tab) => {
  const id = strategyIdFromTab(tab);
  if (id == null) return null;
  return (strategies || []).find((s) => s.id === id) || null;
};

// The strategy a watch with no strategy_id falls back to.
export const fallbackStrategyId = (strategies = []) =>
  strategies.find((s) => s.kind === 'band_reversion')?.id ?? strategies[0]?.id ?? null;

// The stop loss (entry-day σ, 0 = off) of the strategy a watch belongs to — or,
// for a stock with no watch yet, of the one a new watch would join (fp_user's
// ensureStrategy targets the band strategy). The chart and the watch-stats dialog
// backtest under it (issue #417) so they show the trades the scans would take.
// `strategies` is GET /api/signal-watches' list; none (signed out) → the default.
export const stopKOfWatch = (strategies = [], watch = null) => {
  const id = watch?.strategy_id ?? fallbackStrategyId(strategies);
  return resolveStopK(strategies.find((s) => s.id === id)?.stopK);
};

// Predicate: does this watch / event / position / trade row belong to `strategy`?
export const ownedByStrategy = (strategy, strategies) => {
  const fallback = fallbackStrategyId(strategies);
  return (row) => (row.strategy_id ?? fallback) === strategy.id;
};

// Distinct tickers currently opted into `strategy`, in the watches' own order.
export const strategySymbols = (watches, strategy, strategies) =>
  [...new Set((watches || []).filter(ownedByStrategy(strategy, strategies)).map((w) => w.ticker).filter(Boolean))];

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

// One caption for the dropdown row and the list header: "12 stocks watched · 3 of 5 open".
export const strategyCaption = (strategy) =>
  `${plural(strategy.watchCount ?? 0, 'stock')} watched · ${strategy.openPositions ?? 0} of ${strategy.positionCap ?? '?'} open`;
